import { useGetBackProcedimenti } from '@framework/api-client/api/backoffice/procedimenti/procedimenti.ts';
import { type GetBackProcedimentiParams } from '@framework/api-client/api/backoffice/schemas/getBackProcedimentiParams.ts';
import { GetBackProcedimentiTipologia } from '@framework/api-client/api/backoffice/schemas/getBackProcedimentiTipologia.ts';
import { type Procedimento } from '@framework/api-client/api/backoffice/schemas/procedimento.ts';
import { type CampoSemantico } from '@framework/api-client/api/inquiries-compile/schemas/campoSemantico.ts';
import { GetTagSemanticiTipoSezione } from '@framework/api-client/api/users/schemas/getTagSemanticiTipoSezione.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { useDossierSearchableFields } from '@framework/common-web/schema/dossiers/searchable/useDossierSearchableFields.ts';
import { useDossierPaginationChange } from '@framework/common-web/schema/dossiers/table/useDossierPaginationChange.ts';
import { useProcedureSearchableSchema } from '@framework/common-web/schema/procedure/hooks/useProcedureSearchableSchema.tsx';
import { getSemanticTagColumn } from '@framework/common-web/schema/semantic-tags/getSemanticTagColumn.ts';
import { useSemanticTagSerchableExtension } from '@framework/common-web/schema/semantic-tags/hooks/useSemanticTagSerchableExtension.ts';
import { TitleSubmissionRow } from '@framework/common-web/ui/inquiry/submission/TitleSubmissionRow.tsx';
import { FilterBar } from '@framework/ui/components/search/filter-bar/FilterBar.tsx';
import { SwitchContenSizeButton } from '@framework/ui/components/table/column-picker/content-size/SwitchContenSizeButton.tsx';
import { useTableContentSize } from '@framework/ui/components/table/column-picker/content-size/useTableContentSize.ts';
import { usePickedColumns } from '@framework/ui/components/table/column-picker/usePickedColumns.tsx';
import { ColumnSortDirections } from '@framework/ui/components/table/TableColumnSort.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { getPaginationUrlColumns } from '@framework/ui/core/pagination/paginationUrlColumns.ts';
import { usePaginationUrlQueryState } from '@framework/ui/core/pagination/paginationUrlQuery.ts';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import {
  type BrandedPath,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { Divider, Space } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { useTheme } from 'styled-components';
import { returnFocusToPageTitle } from '../../../ui/layout/header/returnFocusToPageTitle.tsx';
import { PageLayout } from '../../../ui/layout/PageLayout.tsx';
import { NetworkAlertPage } from '../../errors/network-alert/NetworkAlertPage.tsx';
import { LoadingPage } from '../../loading/LoadingPage.tsx';
import { DossierResponsiveCard } from '../components/cards/DossierResponsiveCard.tsx';
import { getDossiersColumns } from './dossiersColumns.tsx';

const PAGE_TITLE_ID = 'dossier-list-title-focus-id';

type IDossierListPageProps = RouteComponentProps;

const defaultQuery: GetBackProcedimentiParams = {
  tipologia: GetBackProcedimentiTipologia.PRATICA,
  page: 1,
  pageSize: 10,
  sortBy: 'DATA_ULTIMA_MODIFICA',
  sortDirection: ColumnSortDirections.descend,
};

export function DossierListPage(props: IDossierListPageProps) {
  const history = useHistory();
  const isMobile = useBreakpoint(down('md')) ?? false;
  const { t } = useTranslation();
  const user = useCurrentUser();
  const theme = useTheme();

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: t('dossiers-list.richieste', 'Pratiche'),
  });

  const [query, setQuery] =
    usePaginationUrlQueryState<GetBackProcedimentiParams>({
      history,
      input: defaultQuery,
    });

  const { data, isLoading, isFetching, error } = useGetBackProcedimenti(query, {
    query: { keepPreviousData: true },
  });

  const dossiers = useMemo(() => {
    return data?.data?.procedimentos ?? [];
  }, [data?.data?.procedimentos]);

  const meta = useMemo(
    () => ({ totale: data?.data?.totale }),
    [data?.data?.totale],
  );

  // Crea un array di "campiSemantici" rimuovendo tutti gli elementi non definiti
  const semanticFieldList = useMemo(
    () =>
      dossiers
        ?.flatMap(s => s.campiSemantici)
        .filter((c): c is CampoSemantico => Boolean(c)) ?? [],

    [dossiers],
  );

  const semanticTagSearchableOptions = useSemanticTagSerchableExtension({
    sectionType: GetTagSemanticiTipoSezione.BACKOFFICE,
  });

  const searchableFields = useDossierSearchableFields({
    extraFields: semanticTagSearchableOptions.searchableFields,
  });

  const { contentSize, setContentSize } = useTableContentSize(
    `dossier-fo-content-size-user-preference-${
      user?.utenteLoggato?.idUtente ?? 'guest'
    }`,
  );

  const dossiersColumns = useMemo(
    () => getDossiersColumns(isMobile),
    [isMobile],
  );

  const extraColumns = useMemo(
    () =>
      semanticTagSearchableOptions.semanticTags.map(t =>
        getSemanticTagColumn(t, GetTagSemanticiTipoSezione.FRONTOFFICE),
      ),
    [semanticTagSearchableOptions.semanticTags],
  );

  const { columns: customColumns, fixedColumnProps } = usePickedColumns(
    dossiersColumns,
    {
      id: `dossier-fo-list-${user?.utenteLoggato?.idUtente ?? 'guest'}`,
      extra: (
        <SwitchContenSizeButton
          onChangeSize={size => setContentSize(size)}
          currentContentSize={contentSize}
        />
      ),
      extraColumns,
    },
  );

  const columns = getPaginationUrlColumns<
    Procedimento,
    GetBackProcedimentiParams
  >(customColumns, query);

  const onPaginationChange = useDossierPaginationChange(query, setQuery);

  const searchableSchema = useProcedureSearchableSchema({
    extraShape: semanticTagSearchableOptions.schemaShape,
  });

  if (isLoading) return <LoadingPage />;

  if (error) {
    return (
      <NetworkAlertPage
        message={t(
          'dossier.list.errors.unable-to-load',
          "Impossibile caricare l'elenco delle pratiche",
        )}
        error={error}
      />
    );
  }

  const filterBar = (
    <FilterBar<GetBackProcedimentiParams>
      id="dossier-list"
      schema={searchableSchema}
      fields={searchableFields}
      query={query}
      setQuery={setQuery}
      defaultQuery={defaultQuery}
      pageKey={'page'}
      pageDefaultIndex={1}
      transform={semanticTagSearchableOptions.transform}
      ignoredKeys={['page', 'pageSize', 'sortBy', 'sortDirection']}
      isFetching={isFetching}
      total={meta?.totale ?? undefined}
      layout={{ showInCard: true }}
      messageListProps={{
        message: t(
          'submission-list.domande-trovate-in-base-ai-filtri',
          'domande trovate in base ai filtri di ricerca.',
        ),
        resetButtonProps: {
          onClick: () => {
            setQuery(defaultQuery);
            returnFocusToPageTitle(PAGE_TITLE_ID);
          },
          children: t('submission-list.rimuovi-filtri', 'Rimuovi filtri'),
        },
      }}
    />
  );

  return (
    <PageLayout isTablePage className="dossier-page-header">
      <DossierResponsiveCard
        hideCardHeader={theme.searchButtonOnTableFlag}
        noPadding
        title={
          <TitleSubmissionRow>
            <Space>
              {/* <Avatar icon={<FolderOutlined />} size="large" />
              <FolderOutlined /> */}
              {t('dossiers-list.dossierss-list-title', 'Elenco Pratiche')}
            </Space>
          </TitleSubmissionRow>
        }
      >
        {!theme.searchButtonOnTableFlag && <Divider style={{ margin: 0 }} />}

        <TableData
          title={() => <div>{filterBar}</div>}
          darkTitleBar={false}
          {...fixedColumnProps}
          showSorterTooltip={false}
          size="small"
          dataSource={dossiers}
          loading={isLoading || isFetching}
          pagination={{
            current: Number(query.page ?? 1),
            pageSize: query.pageSize != null ? Number(query.pageSize) : 10,
            total: meta?.totale ?? 0,
            pageSizeOptions: [5, 10, 20, 50],
          }}
          onChange={onPaginationChange}
          columns={columns}
        />
      </DossierResponsiveCard>
    </PageLayout>
  );
}
